import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { contractService } from '../../services/contractService';
import type { Contract, Deliverable, Milestone, Payment, Clause, ContractTemplate } from '@shared/types';
import { DetailsStep } from './DetailsStep';
import { TemplateStep } from './TemplateStep';
import { ClausesStep } from './ClausesStep';
import { DeliverablesStep } from './DeliverablesStep';
import { MilestonesStep } from './MilestonesStep';
import { PaymentsStep } from './PaymentsStep';
import { ScopePricingStep } from './ScopePricingStep';
import { PreviewStep } from './PreviewStep';
import { ZohoEstimateImportModal } from './ZohoEstimateImportModal';
import type { EstimatePrefill } from '../../services/zohoBooksService';
import { useClients } from '../../hooks/useClients';
import { useTemplates } from '../../hooks/useTemplates';

interface ContractWizardProps {
  existingContract?: Contract;
  isEditing?: boolean;
}

export function ContractWizard({ existingContract, isEditing = false }: ContractWizardProps = {}) {
  const navigate = useNavigate();
  const { templates } = useTemplates();
  const [currentStep, setCurrentStep] = useState(isEditing ? 1 : 0); // Start at Details step when editing
  const [saving, setSaving] = useState(false);
  const [showZohoEstimate, setShowZohoEstimate] = useState(false);
  const { clients } = useClients();

  // Calculate default expiration date (30 days from today)
  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };
  
  const [formData, setFormData] = useState<Partial<Contract> & { selectedTemplate?: ContractTemplate }>({
    clientName: '',
    executiveSummary: '',
    deliverables: [],
    milestones: [],
    payments: [],
    clauses: [],
    expirationDate: getDefaultExpirationDate(),
    paymentScheduleType: 'one-time',
  });

  // Pre-populate form data when editing
  useEffect(() => {
    if (existingContract && templates.length > 0) {
      const template = templates.find(t => t.id === existingContract.templateId);
      setFormData({
        ...existingContract,
        selectedTemplate: template,
      });
    }
  }, [existingContract, templates]);

  // Dynamically build steps based on selected template
  const steps = useMemo(() => {
    const template = formData.selectedTemplate;
    const baseSteps = ['Template'];
    
    if (!template) return baseSteps; // Only show template selection until template is chosen

    // Add steps based on template configuration
    baseSteps.push('Details');
    
    // Use combined Scope & Pricing step for templates with deliverables or payments
    if (template.hasDeliverables || template.hasPaymentSchedule) {
      baseSteps.push('Scope & Pricing');
    }
    
    baseSteps.push('Preview');
    
    return baseSteps;
  }, [formData.selectedTemplate]);

  const updateFormData = (updates: Partial<Contract>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Apply a Zoho estimate's pre-fill into the wizard's form state. Preserves
  // the user's template selection and anything they've explicitly typed
  // before opening the modal (the modal is also offered later in the flow).
  const handleEstimatePrefill = (prefill: EstimatePrefill) => {
    setFormData((prev) => {
      const merged: typeof prev = { ...prev };
      if (prefill.clientName) merged.clientName = prefill.clientName;
      if (prefill.title) merged.title = prefill.title;
      if (prefill.executiveSummary && !prev.executiveSummary) {
        merged.executiveSummary = prefill.executiveSummary;
      }
      if (prefill.expirationDate) merged.expirationDate = prefill.expirationDate;
      if (prefill.estimatedStartDate) merged.estimatedStartDate = prefill.estimatedStartDate;
      if (typeof prefill.service_cost === 'number') merged.serviceCost = prefill.service_cost;
      if (prefill.deliverables && prefill.deliverables.length > 0) {
        merged.deliverables = prefill.deliverables as Deliverable[];
      }
      if (prefill.metadata) {
        merged.metadata = { ...(prev.metadata ?? {}), ...prefill.metadata };
      }
      return merged;
    });
  };

  // If the wizard already has a client selected and it's a Zoho-sourced one,
  // we can filter the estimate list to that customer's estimates only.
  const selectedClientExternalId = useMemo(() => {
    const c = clients.find((c) => c.name === formData.clientName);
    return c?.externalSource === 'zoho_books' ? c.externalId : undefined;
  }, [clients, formData.clientName]);


  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepName: string) => {
    const stepIndex = steps.indexOf(stepName);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    }
  };

  const canProceed = () => {
    const stepName = steps[currentStep];
    
    // Validate template is selected before proceeding from Template step
    if (stepName === 'Template' && !formData.selectedTemplate) {
      toast.error('Please select a template to continue');
      return false;
    }
    
    // Validate required fields in Details step
    if (stepName === 'Details') {
      // Only validate project name for SOW templates (hasScope)
      if (formData.selectedTemplate?.hasScope && !formData.title?.trim()) {
        toast.error('Please enter a project name');
        return false;
      }
      if (!formData.clientName?.trim()) {
        toast.error('Please select a client');
        return false;
      }
      if (!formData.expirationDate) {
        toast.error('Please set an expiration date');
        return false;
      }
    }
    
    // Validate Scope & Pricing step
    if (stepName === 'Scope & Pricing') {
      if (!formData.serviceCost || formData.serviceCost <= 0) {
        toast.error('Please enter a service cost');
        return false;
      }
      if (formData.selectedTemplate?.hasDeliverables && (!formData.deliverables || formData.deliverables.length === 0)) {
        toast.error('Please add at least one deliverable');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    const toastId = toast.loading(isEditing ? 'Updating contract...' : 'Creating contract...');

    // Resolve client_id from the picked client name so the contract is
    // properly linked. Otherwise the client detail page won't find it
    // (it filters by clientId). Picks the unique match by name.
    const matchedClient = clients.find((c) => c.name === formData.clientName);
    const payload = matchedClient && !formData.clientId
      ? { ...formData, clientId: matchedClient.id }
      : formData;

    try {
      let contract;
      if (isEditing && existingContract) {
        contract = await contractService.updateContract(existingContract.id, payload);
        toast.success('Contract updated successfully!', { id: toastId });
      } else {
        contract = await contractService.createContract(payload);
        toast.success('Contract created successfully!', { id: toastId });
      }
      navigate(`/contracts/${contract.id}`);
    } catch (error) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} contract:`, error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} contract. Please try again.`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    const stepName = steps[currentStep];
    
    switch (stepName) {
      case 'Template':
        return <TemplateStep data={formData} onUpdate={updateFormData} />;
      case 'Details':
        return (
          <DetailsStep
            data={formData}
            onUpdate={updateFormData}
            template={formData.selectedTemplate}
          />
        );
      case 'Scope & Pricing':
        return <ScopePricingStep data={formData} onUpdate={updateFormData} template={formData.selectedTemplate} />;
      case 'Preview':
        return <PreviewStep data={formData} template={formData.selectedTemplate} onEditStep={goToStep} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{isEditing ? 'Edit Contract' : 'Create New Contract'}</h1>
            <p className="mt-2 text-gray-600">{isEditing ? 'Update your contract details below' : 'Follow the steps below to create your contract'}</p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setShowZohoEstimate(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 shrink-0"
              title="Pre-fill the wizard from a Zoho Books estimate"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Import from Zoho Estimate
            </button>
          )}
        </div>

        {/* Progress Steps — compact "Step X of N" on mobile, full visual on md+ */}
        <div className="mb-6 sm:mb-8">
          {/* Mobile: compact indicator */}
          <div className="md:hidden bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-xs text-gray-500">
                {Math.round(((currentStep + 1) / steps.length) * 100)}%
              </span>
            </div>
            <p className="text-base font-semibold text-gray-900">{steps[currentStep]}</p>
            <div className="mt-3 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Desktop: full circles + connector */}
          <div className="hidden md:flex items-center justify-center">
            {steps.map((step, index) => (
              <div key={step} className="flex items-center">
                {/* Step Circle */}
                <div className="flex flex-col items-center">
                  <div
                    className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-200 ${
                      index < currentStep
                        ? 'bg-primary-600 border-primary-600'
                        : index === currentStep
                        ? 'bg-primary-600 border-primary-600 ring-4 ring-primary-100'
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    {index < currentStep ? (
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span
                        className={`text-lg font-semibold ${
                          index === currentStep ? 'text-white' : 'text-gray-500'
                        }`}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-center">
                    <p
                      className={`text-sm font-medium whitespace-nowrap ${
                        index <= currentStep ? 'text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {step}
                    </p>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="w-12 lg:w-24 h-0.5 mx-2 lg:mx-4 mb-8">
                    <div
                      className={`h-full transition-all duration-300 ${
                        index < currentStep ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6 md:p-8">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-6 sm:mt-8 flex flex-wrap justify-between gap-3">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className="px-4 sm:px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <button
              onClick={() => navigate('/contracts')}
              className="px-4 sm:px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {currentStep === steps.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 sm:px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Contract' : 'Create Contract')}
              </button>
            ) : (
              <button
                onClick={() => canProceed() && nextStep()}
                className="px-4 sm:px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>

      <ZohoEstimateImportModal
        isOpen={showZohoEstimate}
        onClose={() => setShowZohoEstimate(false)}
        customerExternalId={selectedClientExternalId}
        onImported={handleEstimatePrefill}
      />
    </div>
  );
}

